import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { EditableText } from '../content/EditableText';
import { useCmsContent } from '../content/CmsContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookAppointment = () => {
  const { toast } = useToast();
  const { content, editing, update } = useCmsContent();
  const copy = content.pages.bookAppointment;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    serviceType: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) return;
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/appointments`, formData);
      
      toast({
        title: copy.successTitle,
        description: response.data.message || copy.successDescription,
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        preferredDate: '',
        preferredTime: '',
        serviceType: '',
        message: ''
      });
    } catch (error) {
      console.error('Appointment booking error:', error);
      toast({
        title: copy.errorTitle,
        description: error.response?.data?.detail || copy.errorDescription,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const editableInputProps = (contentKey, publicValue) => editing
    ? {
      value: copy[contentKey],
      placeholder: '',
      onChange: (event) => update(`pages.bookAppointment.${contentKey}`, event.target.value),
      className: 'cms-editable-input text-gray-500',
    }
    : {
      value: publicValue,
      placeholder: copy[contentKey],
      onChange: handleChange,
    };

  return (
    <div className="min-h-screen bg-gradient-to-b from-brand-50 to-white">
      {/* Hero Section */}
      <section className="bg-brand-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-light mb-4"><EditableText contentKey="pages.bookAppointment.heroTitle" /></h1>
          <p className="text-xl">
            <EditableText contentKey="pages.bookAppointment.heroBody" />
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="flex items-center mb-2">
                    <User className="h-4 w-4 mr-2" />
                    <EditableText contentKey="pages.bookAppointment.firstNameLabel" multiline={false} />
                  </Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    {...editableInputProps('firstNamePlaceholder', formData.firstName)}
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="mb-2 block">
                    <EditableText contentKey="pages.bookAppointment.lastNameLabel" multiline={false} />
                  </Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    {...editableInputProps('lastNamePlaceholder', formData.lastName)}
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="flex items-center mb-2">
                    <Mail className="h-4 w-4 mr-2" />
                    <EditableText contentKey="pages.bookAppointment.emailLabel" multiline={false} />
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    {...editableInputProps('emailPlaceholder', formData.email)}
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="flex items-center mb-2">
                    <Phone className="h-4 w-4 mr-2" />
                    <EditableText contentKey="pages.bookAppointment.phoneLabel" multiline={false} />
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    {...editableInputProps('phonePlaceholder', formData.phone)}
                  />
                </div>
              </div>

              {/* Appointment Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="preferredDate" className="flex items-center mb-2">
                    <Calendar className="h-4 w-4 mr-2" />
                    <EditableText contentKey="pages.bookAppointment.dateLabel" multiline={false} />
                  </Label>
                  <Input
                    id="preferredDate"
                    name="preferredDate"
                    type="date"
                    required
                    value={formData.preferredDate}
                    onChange={handleChange}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label htmlFor="preferredTime" className="flex items-center mb-2">
                    <Clock className="h-4 w-4 mr-2" />
                    <EditableText contentKey="pages.bookAppointment.timeLabel" multiline={false} />
                  </Label>
                  <select
                    id="preferredTime"
                    name="preferredTime"
                    required
                    value={formData.preferredTime}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  >
                    <option value="">{copy.timePlaceholder}</option>
                    <option value="morning">{copy.morning}</option>
                    <option value="afternoon">{copy.afternoon}</option>
                    <option value="evening">{copy.evening}</option>
                  </select>
                </div>
              </div>

              {/* Service Type */}
              <div>
                <Label htmlFor="serviceType" className="mb-2 block">
                  <EditableText contentKey="pages.bookAppointment.serviceLabel" multiline={false} />
                </Label>
                <select
                  id="serviceType"
                  name="serviceType"
                  required
                  value={formData.serviceType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">{copy.servicePlaceholder}</option>
                  <option value="individual">{copy.individual}</option>
                  <option value="couples">{copy.couples}</option>
                  <option value="family">{copy.family}</option>
                  <option value="consultation">{copy.consultation}</option>
                </select>
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message" className="flex items-center mb-2">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  <EditableText contentKey="pages.bookAppointment.messageLabel" multiline={false} />
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  {...editableInputProps('messagePlaceholder', formData.message)}
                />
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand-700 hover:bg-brand-800 text-white py-3 text-lg"
                >
                  {isSubmitting
                    ? <EditableText contentKey="pages.bookAppointment.submitting" multiline={false} />
                    : <EditableText contentKey="pages.bookAppointment.submit" multiline={false} />}
                </Button>
              </div>

              <p className="text-sm text-gray-600 text-center">
                <EditableText contentKey="pages.bookAppointment.confidential" />
              </p>
            </form>
          </div>

          {/* Additional Info */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-brand-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="h-8 w-8 text-brand-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2"><EditableText contentKey="pages.bookAppointment.callUs" /></h3>
              <p className="text-gray-600"><EditableText contentKey="pages.bookAppointment.phone" multiline={false} /></p>
            </div>
            <div className="text-center">
              <div className="bg-brand-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-brand-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2"><EditableText contentKey="pages.bookAppointment.emailUs" /></h3>
              <p className="text-gray-600"><EditableText contentKey="pages.bookAppointment.email" multiline={false} /></p>
            </div>
            <div className="text-center">
              <div className="bg-brand-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-brand-700" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2"><EditableText contentKey="pages.bookAppointment.hours" /></h3>
              <p className="text-gray-600"><EditableText contentKey="pages.bookAppointment.hoursValue" multiline={false} /></p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookAppointment;
